import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {number | null} */
  #reserveTimerInterval = null;
  /** @type {number | null} */
  #shippingTimerInterval = null;
  #reserveDurationMs = 5 * 60 * 1000;
  #shippingDurationMs = 80 * 60 * 1000;
  #reserveStorageKey = 'solcey-cart-reserve-until';
  #shippingStorageKey = 'solcey-cart-ship-cutoff-until';
  #isClearingExpiredCart = false;
  #isUpdatingShippingProtection = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdateState);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#startReserveTimer);
    this.addEventListener(DialogOpenEvent.eventName, this.#startShippingTimer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdateState);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startReserveTimer);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startShippingTimer);
    this.#stopReserveTimer();
    this.#stopShippingTimer();
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const itemCount = event.detail?.data?.itemCount;
    if (typeof itemCount === 'number' && itemCount > 0) {
      window.localStorage.setItem(this.#reserveStorageKey, String(Date.now() + this.#reserveDurationMs));
      this.#stopReserveTimer();

      const { dialog } = /** @type {Refs} */ (this.refs);
      const renderedFromSections = this.#renderCartItemsFromSections(event.detail?.data?.sections);
      const shouldForceRefresh = !renderedFromSections;

      dialog?.classList.remove('cart-drawer--empty');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.showDialog();
          if (shouldForceRefresh) {
            this.#refreshDrawerSection();
          }
        });
      });
      return;
    }

    this.showDialog();
  };

  #handleCartUpdateState = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const itemCount = event.detail?.data?.itemCount;
    if (typeof itemCount === 'number' && itemCount === 0) {
      window.localStorage.removeItem(this.#reserveStorageKey);
      window.localStorage.removeItem(this.#shippingStorageKey);
      this.#stopReserveTimer();
      this.#stopShippingTimer();
      dialog?.classList.add('cart-drawer--empty');
    } else if (typeof itemCount === 'number' && itemCount > 0) {
      dialog?.classList.remove('cart-drawer--empty');
    }
  };

  #handleClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-shipping-protection-toggle]') : null;
    if (!(toggle instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#toggleShippingProtection(toggle);
  };

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #startReserveTimer = () => {
    if (!this.querySelector('[data-cart-reserve-timer]')) return;

    const storedUntil = Number.parseInt(window.localStorage.getItem(this.#reserveStorageKey) || '', 10);
    if (!Number.isFinite(storedUntil)) {
      this.querySelectorAll('[data-cart-reserve-timer]').forEach((node) => {
        node.textContent = '05:00';
      });
      return;
    }

    const reserveUntil = storedUntil;

    const render = async () => {
      const timerNodes = this.querySelectorAll('[data-cart-reserve-timer]');
      if (!timerNodes.length) {
        this.#stopReserveTimer();
        return;
      }

      const remainingMs = Math.max(0, reserveUntil - Date.now());
      const totalSeconds = Math.ceil(remainingMs / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      const formatted = `${minutes}:${seconds}`;
      timerNodes.forEach((node) => {
        node.textContent = formatted;
      });

      if (remainingMs <= 0) {
        this.#stopReserveTimer();
        await this.#clearExpiredCart();
      }
    };

    this.#stopReserveTimer();
    render();
    this.#reserveTimerInterval = window.setInterval(render, 1000);
  };

  #startShippingTimer = () => {
    if (!this.querySelector('[data-cart-ship-timer]')) return;

    const now = Date.now();
    const storedUntil = Number.parseInt(window.localStorage.getItem(this.#shippingStorageKey) || '', 10);
    const shippingUntil = Number.isFinite(storedUntil) && storedUntil > now ? storedUntil : now + this.#shippingDurationMs;
    window.localStorage.setItem(this.#shippingStorageKey, String(shippingUntil));

    const render = () => {
      const timerNodes = this.querySelectorAll('[data-cart-ship-timer]');
      const dateNodes = this.querySelectorAll('[data-cart-ship-date]');
      if (!timerNodes.length) {
        this.#stopShippingTimer();
        return;
      }

      const remainingMs = Math.max(0, shippingUntil - Date.now());
      const totalMinutes = Math.max(0, Math.ceil(remainingMs / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const formatted = `${hours} hr ${minutes} mins`;
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
      const tomorrowLabel = `Tomorrow, ${tomorrow.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

      timerNodes.forEach((node) => {
        node.textContent = formatted;
      });
      dateNodes.forEach((node) => {
        node.textContent = tomorrowLabel;
      });

      if (remainingMs <= 0) {
        const nextUntil = Date.now() + this.#shippingDurationMs;
        window.localStorage.setItem(this.#shippingStorageKey, String(nextUntil));
        this.#stopShippingTimer();
        this.#startShippingTimer();
      }
    };

    this.#stopShippingTimer();
    render();
    this.#shippingTimerInterval = window.setInterval(render, 60000);
  };

  #stopReserveTimer() {
    if (this.#reserveTimerInterval) {
      window.clearInterval(this.#reserveTimerInterval);
      this.#reserveTimerInterval = null;
    }
  }

  #stopShippingTimer() {
    if (this.#shippingTimerInterval) {
      window.clearInterval(this.#shippingTimerInterval);
      this.#shippingTimerInterval = null;
    }
  }

  async #clearExpiredCart() {
    if (this.#isClearingExpiredCart) return;

    this.#isClearingExpiredCart = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, fetchConfig('json', { body }));
      const parsedResponse = await response.json();
      window.localStorage.removeItem(this.#reserveStorageKey);
      window.localStorage.removeItem(this.#shippingStorageKey);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponse, 'cart-drawer-component', {
          itemCount: 0,
          source: 'cart-drawer-expiry',
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#isClearingExpiredCart = false;
    }
  }

  async #refreshDrawerSection() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : '';
    if (!sectionId) return;

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
      const { dialog } = /** @type {Refs} */ (this.refs);
      if (this.querySelector('[ref="cartItemCount"]')) {
        dialog?.classList.remove('cart-drawer--empty');
      }
    } catch (error) {
      console.error(error);
    }
  }

  #renderCartItemsFromSections(sections) {
    const currentCartItems = this.querySelector('cart-items-component');
    const sectionId = currentCartItems instanceof HTMLElement ? currentCartItems.dataset.sectionId : '';
    const sectionHtml = sectionId ? sections?.[sectionId] : null;

    if (!(currentCartItems instanceof HTMLElement) || typeof sectionHtml !== 'string') {
      return false;
    }

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextCartItems =
      parsed.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
      parsed.querySelector('cart-items-component');

    if (!(nextCartItems instanceof HTMLElement)) {
      return false;
    }

    morph(currentCartItems, nextCartItems);
    return true;
  }

  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  #getItemCountFromSections(sections) {
    if (!sections) return 0;

    for (const sectionHtml of Object.values(sections)) {
      if (typeof sectionHtml !== 'string') continue;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const count = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      const itemCount = count ? Number.parseInt(count, 10) : NaN;

      if (Number.isFinite(itemCount)) {
        return itemCount;
      }
    }

    return 0;
  }

  async #toggleShippingProtection(toggle) {
    if (this.#isUpdatingShippingProtection || toggle.disabled) return;

    const variantId = toggle.dataset.variantId;
    const currentLine = Number.parseInt(toggle.dataset.line || '', 10);
    const shouldAdd = toggle.getAttribute('aria-pressed') !== 'true';
    const sectionsToUpdate = this.#getSectionsToUpdate();

    if (shouldAdd && !variantId) return;
    if (!shouldAdd && !Number.isFinite(currentLine)) return;

    this.#isUpdatingShippingProtection = true;
    toggle.disabled = true;

    try {
      let parsedResponse;

      if (shouldAdd) {
        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionsToUpdate.join(','),
          sections_url: window.location.pathname,
          properties: {
            _shipping_protection: 'true',
          },
        });

        const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
        parsedResponse = await response.json();
      } else {
        const body = JSON.stringify({
          line: currentLine,
          quantity: 0,
          sections: sectionsToUpdate.join(','),
          sections_url: window.location.pathname,
        });

        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        parsedResponse = await response.json();
      }

      if (parsedResponse?.errors) {
        throw new Error(parsedResponse.errors);
      }

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponse, 'cart-drawer-shipping-protection', {
          itemCount: this.#getItemCountFromSections(parsedResponse.sections),
          source: 'cart-drawer-shipping-protection',
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#isUpdatingShippingProtection = false;
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
